import { useLayoutEffect, useRef, useState } from 'react';
import { getContainMapping, normToContainerPercent } from '../utils/overlayMap';
import './ResultEvidencePreview.css';

function spotKindLabel(spot) {
  if (spot?.isKnot || spot?.type === 'wood_knot') return 'ตาไม้';
  return 'จุดด่างดำ';
}

export default function ResultEvidencePreview({
  imageUrl,
  overlaySpots = [],
  selectedKeys = new Set(),
  onToggleSpot,
  readOnly = false,
  spotCount = 0,
  verdict = 'OK',
}) {
  const rootRef = useRef(null);
  const imgRef = useRef(null);
  const [mappedSpots, setMappedSpots] = useState([]);

  useLayoutEffect(() => {
    const img = imgRef.current;
    const root = rootRef.current;
    if (!img || !root) return undefined;

    const update = () => {
      // รูปใช้ object-fit: contain — ต้อง map แบบ contain ไม่ใช่ cover
      const mapping = getContainMapping(img, root);
      if (!mapping || !overlaySpots?.length) {
        setMappedSpots([]);
        return;
      }

      setMappedSpots(
        overlaySpots
          .map((spot, index) => {
            const box = normToContainerPercent(
              { nx: spot.nx, ny: spot.ny, nw: spot.nw, nh: spot.nh },
              mapping
            );
            if (!box) return null;
            const rowKey = spot.rowKey || `${spot.id || 'spot'}::${index}`;
            if (!selectedKeys.has(rowKey)) return null;
            return {
              ...spot,
              ...box,
              rowKey,
              index,
            };
          })
          .filter(Boolean)
          .map((spot, displayIndex) => ({ ...spot, displayIndex }))
      );
    };

    update();
    img.addEventListener('load', update);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    if (ro) ro.observe(root);
    window.addEventListener('resize', update);

    return () => {
      img.removeEventListener('load', update);
      if (ro) ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [imageUrl, overlaySpots, selectedKeys]);

  const isNg = verdict === 'NG';

  return (
    <div ref={rootRef} className="result-evidence-preview">
      {imageUrl ? (
        <img ref={imgRef} src={imageUrl} alt={`หลักฐานพบ ${spotCount} จุด`} className="result-evidence-img" />
      ) : (
        <div className="result-photo-empty muted">ไม่มีรูปหลักฐาน</div>
      )}

      <div className="result-evidence-banner">
        <span className={isNg ? 'ng' : 'ok'}>
          พบ {spotCount} จุด · {verdict}
        </span>
        <span className="sub">{isNg ? 'ไม่ผ่านเกณฑ์ Appearance' : 'ผ่านเกณฑ์ Appearance'}</span>
      </div>

      <div className="result-spot-layer">
        {mappedSpots.map((spot) => {
          const cx = spot.left + spot.width / 2;
          const cy = spot.top + spot.height / 2;
          const pad = Math.max(Math.max(spot.width, spot.height) * 0.9, 2.4);
          const boxSide = Math.min(42, Math.max(Math.max(spot.width, spot.height) + pad * 2, 7));
          let left = cx - boxSide / 2;
          let top = cy - boxSide / 2;
          if (left < 0) left = 0;
          if (top < 0) top = 0;
          if (left + boxSide > 100) left = Math.max(0, 100 - boxSide);
          if (top + boxSide > 100) top = Math.max(0, 100 - boxSide);

          const toggle = () => {
            if (!readOnly && onToggleSpot) onToggleSpot(spot.rowKey);
          };

          return (
            <button
              key={spot.rowKey}
              type="button"
              className="result-spot-box"
              style={{
                left: `${left}%`,
                top: `${top}%`,
                width: `${boxSide}%`,
                height: `${boxSide}%`,
              }}
              disabled={readOnly}
              aria-pressed
              title={`${spotKindLabel(spot)} Ø${Number(spot.diameterMm).toFixed(1)}`}
              onClick={toggle}
            >
              <span className="spot-corner tl" />
              <span className="spot-corner tr" />
              <span className="spot-corner bl" />
              <span className="spot-corner br" />
              <span className="spot-label">
                {spot.displayIndex + 1}. {spotKindLabel(spot)} Ø{Number(spot.diameterMm).toFixed(1)}
              </span>
            </button>
          );
        })}
      </div>

      <div className="result-evidence-foot">
        รวม {spotCount} จุดที่นับ · {readOnly ? 'ดูอย่างเดียว' : 'แตะกรอบหรือติ๊กรายการด้านล่าง'}
      </div>
    </div>
  );
}
